import * as React from 'react';
import { createRef } from 'react';
import Dropzone from 'react-dropzone';
import { ILogoProcessingResult } from './logo-upload.component';
import ProgressUploaderComponent from 'src/App/shared/components/progress-uploader.component';
import { IAppContext, withAppContext } from 'src/App/shared/app-context';
import { localization } from 'src/App/shared/localization/localization';
import { CheckIcon } from 'src/App/shared/icons/pt-icon-check';
import { DebuggerInstance } from 'src/App/shared/debugger';
import { UnCheckIcon } from 'src/App/shared/icons/pt-icon-uncheck';
import { Markdown } from 'src/App/shared/localization/markdown-wrapper';
import { LogoClearMethod } from 'src/App/shared/logo.types';


export enum DropZoneState {
    None = 'drop-zone',
    Accept = 'drop-zone-accept',
    Reject = 'drop-zone-reject'
}

export interface IUploadLogoProps {
    appContext: IAppContext;
    apiUrl: string;
    onUploadCompleted(
        sucess: boolean, 
        data: ILogoProcessingResult | null, 
        message: string | null, 
        fileName?: string): void;
}

interface IUploadLogoState {
    uploadStarted: boolean;
    uploadFailed: boolean;
    uploadErrorMessage: string | null;
    selecteClearMethod: LogoClearMethod;
    fileName?: string;
}

const acceptedExtensions: string[] = ['png', 'ai', 'eps', 'pdf', 'cdr', 'psd', 'tiff', 'tif', 'svg'];
const maxSize: number = 12; // in MB

class UploadLogoComponent extends React.PureComponent<IUploadLogoProps,IUploadLogoState> {
    private uploader: ProgressUploaderComponent<ILogoProcessingResult> | null;
    private dropzoneRef: React.RefObject<Dropzone> = createRef<Dropzone>();

    constructor(props: IUploadLogoProps) {
        super(props);

        this.state = {
            uploadStarted: false,
            uploadFailed: false,
            uploadErrorMessage: null,
            selecteClearMethod: LogoClearMethod.Full
        };
    }

    private onDropFiles(accepted: File[]): void {
        if (accepted.length === 0) {
            return;
        }

        if (this.uploader === null) {
            DebuggerInstance.error('App is not in valid state! Uploader component is missing!');
            return;
        }

        const file = accepted[0]; // single file is expected

        // validation of file extension
        const ext = file.name.split('.').pop();
        if(ext === undefined || !this.validateExtension(ext)) {
            // this.uploader.setState({
            //     uploadStatus: UploadStatus.FailExt,
            //     uploadStatusParam: ext || '?',
            //     uploadProgress: 100
            // });
            const msg = localization.formatString(
                localization.uploadLoc.progressUpl.failExtension,
                ext || '?'
            ) as string;
            this.setState({
                uploadErrorMessage: msg
            });

            DebuggerInstance.error(`invalid extension ${ext}`);
            return;
        }

        // validation of file max size
        const size = file.size / (1024 * 1024); // size in MB
        if (size > maxSize) {
            // this.uploader.setState({
            //     uploadStatus: UploadStatus.FailTooBig,
            //     uploadStatusParam: maxSize.toString(),
            //     uploadProgress: 100
            // });
            const msg = localization.formatString(
                localization.uploadLoc.progressUpl.failTooBig,
                maxSize.toString()
            ) as string;
            this.setState({
                uploadErrorMessage: msg
            });

            DebuggerInstance.error(`file is too big ${size}`);
            return;
        }

        this.setState({
            uploadStarted: true,
            uploadErrorMessage: null,
            fileName: file.name
        });
        this.uploader.startUpload(file, this.props.apiUrl, this.state.selecteClearMethod);
    }

    private validateExtension(ext: string): boolean {
        ext = ext.toLowerCase();
        return acceptedExtensions.indexOf(ext) >= 0;
    }

    private handleOpenUpload() {
        if (this.dropzoneRef && this.dropzoneRef.current) {
            this.dropzoneRef.current.open();
        }
    }

    private getUploadDropZone(): JSX.Element {
        return (
            <Dropzone ref={this.dropzoneRef}
                disableClick={true}
                className="drop-zone"
                activeClassName="drop-zone-active"
                onDrop={(accepted) => this.onDropFiles(accepted)}>
                <div className="container logo-upload-content">
                    <div className="row row-upload-info">
                        <div className="col">
                            <span><Markdown text={localization.uploadLoc.uploadScreen.info1} /></span>
                        </div>
                    </div>
                    <div className="row row-logo-formats">
                        <div className="col-4">
                            <div className="format-info">
                                <div className="title primary-color"><Markdown text={localization.uploadLoc.uploadScreen.formatsRecommended} /></div>
                                <div className="items">
                                    <ul className="items-list">
                                        <li><CheckIcon /><span>pdf</span></li>
                                        <li><CheckIcon /><span>eps</span></li>
                                        <li><CheckIcon /><span>ai (illustrator)</span></li>
                                        <li><CheckIcon /><span>cdr (corel draw)</span></li>
                                        <li><CheckIcon /><span>svg</span></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="format-info">
                                <div className="title primary-color"><Markdown text={localization.uploadLoc.uploadScreen.formatsRaster} /></div>
                                <div className="items">
                                    <ul className="items-list">
                                        <li><CheckIcon /><span>png</span></li>
                                        <li><CheckIcon /><span>tiff</span></li>
                                        <li><CheckIcon /><span>psd</span></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="format-info">
                                <div className="title primary-color"><Markdown text={localization.uploadLoc.uploadScreen.formatsForbidden} /></div>
                                <div className="items">
                                    <ul className="items-list">
                                        <li><UnCheckIcon /><span className="alert-text">jpg</span></li>
                                    </ul>
                                </div>
                                <div className="items-info">
                                    <Markdown text={localization.uploadLoc.uploadScreen.jpgInfo} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row row-clear-background">
                        <div className="col-4 clear-background-option" onClick={() => this.setState({selecteClearMethod: LogoClearMethod.Full })}>
                            <div className="image-place-holder">
                                <img src="/assets/logo-clear/logo-visualization-full-clear.png" />
                            </div>
                            <div className="selector">
                                <div className={'radio-outter ' + ((this.state.selecteClearMethod === LogoClearMethod.Full) ? 'primary-style' : 'checkmark-unchecked')}>
                                    <div className="radio-inner"></div>
                                </div>
                                <div className="selection-label">
                                    {localization.uploadLoc.uploadScreen.optionFull}
                                </div>
                            </div>
                        </div>
                        <div className="col-4 clear-background-option" onClick={() => this.setState({selecteClearMethod: LogoClearMethod.Edge })}>
                            <div className="image-place-holder">
                                <img src="/assets/logo-clear/logo-visualization-edge-clear.png" />
                            </div>
                            <div className="selector">
                                <div className={'radio-outter ' + ((this.state.selecteClearMethod === LogoClearMethod.Edge) ? 'primary-style' : 'checkmark-unchecked')}>
                                    <div className="radio-inner"></div>
                                </div>
                                <div className="selection-label">
                                    {localization.uploadLoc.uploadScreen.optionEdge}
                                </div>
                            </div>
                        </div>
                        <div className="col-4 clear-background-option" onClick={() => this.setState({selecteClearMethod: LogoClearMethod.None })}>
                            <div className="image-place-holder">
                                <img src="/assets/logo-clear/logo-visualization-no-clear.png" />
                            </div>
                            <div className="selector">
                                <div className={'radio-outter ' + ((this.state.selecteClearMethod === LogoClearMethod.None) ? 'primary-style' : 'checkmark-unchecked')}>
                                    <div className="radio-inner"></div>
                                </div>
                                <div className="selection-label">
                                    {localization.uploadLoc.uploadScreen.optionNone}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row row-buttons">
                        <div className="drag-or-upload">
                            <div className="drag-info primary-color"><Markdown text={localization.uploadLoc.uploadScreen.dragDropInfo} /></div>
                            <div className="or"><Markdown text={localization.uploadLoc.uploadScreen.dragOrClick} /></div>
                            <button className="main-button primary-style"
                                onClick={() => this.handleOpenUpload()}>
                                <Markdown text={localization.uploadLoc.uploadScreen.clickInfo} />
                            </button>
                        </div>
                        {this.state.uploadErrorMessage &&
                        <div className="upload-error-message">
                            {this.state.uploadErrorMessage}
                        </div>
                        }
                    </div>
                    <div className="row row-info">
                        <span><Markdown text={localization.uploadLoc.uploadScreen.limitsSize} />: {maxSize}MB
                        | <Markdown text={localization.uploadLoc.uploadScreen.limitsVector} />: PDF, EPS, AI, CDR, SVG
                        | <Markdown text={localization.uploadLoc.uploadScreen.limitsRaster} />: PNG, TIFF, PSD
                        </span>
                    </div>
                </div>
                <div className="dropzone-overlay">
                    <div className="dropzone-overlay-text">
                        <Markdown text={localization.uploadLoc.uploadScreen.dragOverlayMsg} />
                    </div>
                </div>
            </Dropzone>);
    }

    public render() {
        return (
            <div className="logo-upload-root">
                {!this.state.uploadStarted && this.getUploadDropZone()}
                <div className="lu-row-upload-progress"
                    style={{display: this.state.uploadStarted ? 'block' : 'none'}}>
                    <ProgressUploaderComponent<ILogoProcessingResult>
                        ref={(e) => { DebuggerInstance.log(e); this.uploader = e}}
                        onUploadCompleted={(succ, data, err) => this.props.onUploadCompleted(succ, data, err, this.state.fileName)}
                        onRepeatUpload={() => this.setState({
                            uploadStarted: false,
                            uploadFailed: false
                        })}
                    />
                </div>
            </div>
        );
    }
}

export const UploadLogo = withAppContext(UploadLogoComponent);



// WEBPACK FOOTER //
// ./src/App/logo-app/logo-upload/upload-logo.component.tsx